import { getRequest, postRequest } from 'features/common'
import {
  AIAPIVersion,
  BuilderGenAIAction,
  BuilderGenAIActionResult,
  BuilderGenAIActionTypes,
  BuilderGenAIAgent,
  BuilderGenAIBackendActionTypes,
  BuilderGenAIEnvironment,
  BuilderGenAIResponse,
  BuilderGenAISystemMessage,
  MapConfigForAgent
} from '../types'
import settings from 'settings'

export const DEFAULT_AGENT_ID = 'aiagent/aiagent'
export const DEFAULT_AGENT_VERSION = 'live'

///////////////////////////////////////////////////////////////////////////////
// API REQUESTS

// API v0

/** @deprecated Currently unused under AI API v1, but kept for v0. */
export async function createAgentV0({
  mapId,
  mapConfig,
  userContext,
  environment
}: {
  mapId: string
  mapConfig: MapConfigForAgent
  userContext: string
  environment: BuilderGenAIEnvironment
}): Promise<APICreateAgentResponseV0> {
  const url = `${settings.workspaceApiUrl}/genai/${mapId}/${environment}`
  return await postRequest<APICreateAgentRequestV0, APICreateAgentResponseV0>(url, { mapConfig, userContext })
}

// API v0 + v1

export async function* createThread({
  mapId,
  environment,
  agent,
  mapConfig,
  message,
  agentUseCase,
  agentInstructions,
  token,
  signal
}: {
  mapId: string
  environment: BuilderGenAIEnvironment
  agent: BuilderGenAIAgent
  mapConfig: MapConfigForAgent
  message: string
  agentUseCase: string
  agentInstructions: string
  token?: string
  signal?: AbortSignal
}): AsyncGenerator<BuilderGenAIResponse> {
  if (agent.apiVersion === AIAPIVersion.V0) {
    const url = `${settings.workspaceApiUrl}/genai/${mapId}/newThread/${environment}`
    const { parent } = (await getRequest(url, createRequestInitV0(signal))) as { parent: string }
    for await (const response of sendMessage({ mapId, environment, agent, parent, message, token, signal })) {
      yield { ...response }
    }
    return
  }

  const response = requestV1(
    createURLV1(agent, mapId),
    {
      input: message,
      vars: {
        map_title: mapConfig?.title,
        user_input_instructions: agentInstructions,
        map_configuration: mapConfig,
        use_case: agentUseCase,
        conversation_start_time: new Date().toISOString(),
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    } as APIRequestV1,
    createRequestInitV1(token, signal)
  )

  for await (const chunk of response) {
    for (const builderResponse of toBuilderResponsesV1(chunk)) {
      yield builderResponse
    }
  }
}

export async function* sendMessage({
  mapId,
  environment,
  agent,
  parent,
  message,
  token,
  signal
}: {
  mapId: string
  environment: BuilderGenAIEnvironment
  agent: BuilderGenAIAgent
  parent: string
  message: string
  token?: string
  signal?: AbortSignal
}): AsyncGenerator<BuilderGenAIResponse> {
  if (agent.apiVersion === AIAPIVersion.V0) {
    const url = `${settings.workspaceApiUrl}/genai/${mapId}/conversation/${environment}`
    const response = await postRequest<APIRequestV0, APIResponseV0>(
      url,
      { message, parent },
      createRequestInitV0(signal)
    )
    for (const builderResponse of toBuilderResponsesV0(response, parent)) {
      yield builderResponse
    }
    return
  }

  const response = requestV1(
    createURLV1(agent, mapId),
    {
      parent,
      input: message
    } as APIRequestV1,
    createRequestInitV1(token, signal)
  )

  for await (const chunk of response) {
    for (const builderResponse of toBuilderResponsesV1(chunk)) {
      yield builderResponse
    }
  }
}

export async function* sendActionsResult({
  mapId,
  environment,
  agent,
  parent,
  actions,
  token,
  signal
}: {
  mapId: string
  environment: BuilderGenAIEnvironment
  agent: BuilderGenAIAgent
  parent: string
  actions: BuilderGenAIActionResult[]
  token?: string
  signal?: AbortSignal
}): AsyncGenerator<BuilderGenAIResponse> {
  if (agent.apiVersion === AIAPIVersion.V0) {
    const url = `${settings.workspaceApiUrl}/genai/${mapId}/action/${environment}`
    const response = await postRequest<unknown, APIResponseV0>(
      url,
      {
        actions: actions.map(({ id, result }) => ({ parent: id, result }))
      } as APISendActionsRequestV0,
      createRequestInitV0(signal)
    )
    for (const builderResponse of toBuilderResponsesV0(response, parent)) {
      yield builderResponse
    }
    return
  }

  const response = requestV1(
    createURLV1(agent, mapId),
    {
      parent,
      actions: actions.map(({ id, result }) => ({ id, value: result }))
    } as APIRequestV1,
    createRequestInitV1(token, signal)
  )

  for await (const chunk of response) {
    for (const builderResponse of toBuilderResponsesV1(chunk)) {
      yield builderResponse
    }
  }
}

///////////////////////////////////////////////////////////////////////////////
// API UTILITIES

// API v0

function createRequestInitV0(signal?: AbortSignal): RequestInit {
  return { signal }
}

// API v1

function createURLV1(agent: BuilderGenAIAgent, mapId: string): string {
  const search = new URLSearchParams({ mapId })
  if (agent.version !== DEFAULT_AGENT_VERSION) {
    search.append('version', agent.version)
  }
  return `${settings.aiApiUrl}/feature/${agent.id}?${search.toString()}`
}

function createRequestInitV1(token?: string, signal?: AbortSignal): RequestInit {
  return token ? { headers: { Authorization: `Bearer ${token}` }, signal } : { signal }
}

/**
 * Stream responses from /feature/<feature>/streaming, sent as ndjson. Deltas
 * include incremental message fragments. Response chunks gives a complete
 * message, replacing any preceeding deltas. Responses may also include thread
 * ID, actions, and other metadata.
 */
async function* requestV1(url: string, payload: unknown, opts?: RequestInit): AsyncGenerator<APIResponseV1> {
  let complete = false

  // Stream each line/chunk of ndjson response.
  const body = await postRequest(url, payload, opts, (res) => res.body as ReadableStream<Uint8Array>)
  for await (const chunk of ndjsonReadableStream<APIResponseV1>(body)) {
    switch (chunk.type) {
      case 'delta':
      case 'response':
      case 'action_execution':
      case 'error':
        yield chunk
        break
      case 'end':
        complete = true
        break
    }
  }

  // If stream finishes without 'end' chunk, response may have been interrupted.
  if (!complete) {
    yield { type: 'error', message: 'Connection interrupted' }
  }
}

/**
 * Returns an async generator yielding JSON chunks, given an input stream containing
 * only newline-delimited json (ndjson). Based on the `ndjson-readablestream` module
 * from npm (MIT License), with types and minor changes in text decoding.
 *
 * See: https://github.com/pamelafox/ndjson-readablestream/pull/16
 */
async function* ndjsonReadableStream<T = unknown>(byteStream: ReadableStream<Uint8Array>): AsyncGenerator<T> {
  const textStream = byteStream.pipeThrough(new TextDecoderStream('utf-8'))
  const reader = textStream.getReader()

  // Values from `reader.read()` do not necessarily align with newline delimiters;
  // long lines/chunks may be split across multiple stream reads. If we don't have
  // a valid JSON object yet, keep reading the stream.
  let runningText = ''

  while (true) {
    const { done, value } = await reader.read()

    // Stop reading if stream is complete.
    if (done) break

    for (const line of value.split('\n')) {
      try {
        runningText += line
        const result = JSON.parse(runningText)
        yield result
        runningText = ''
      } catch (e) {
        // Not a valid JSON object yet, wait for next chunk.
      }
    }
  }
}

///////////////////////////////////////////////////////////////////////////////
// TYPES
//
// API response structure changes regularly. DO NOT export these
// types outside the current file, instead convert responses into
// one of the stable BuilderGenAI types from `../types`.
//
// V0 Reference: ???
// V1 Reference: https://cartodb.getoutline.com/doc/ai-api-messages-flow-0hJgtZLagv

// API v0

type APICreateAgentRequestV0 = Record<string, unknown>

type APICreateAgentResponseV0 = {
  agent: string
  createdAt: string
}

type APIActionV0 = {
  action: BuilderGenAIActionTypes
  params?: Record<string, unknown>
  parent: string
  isCompleted?: boolean
}

type APIActionResultV0 = {
  parent: string
  result: unknown
}

type APIResponseV0 = {
  message?: string
  actions?: APIActionV0[]
}

type APIRequestV0 = {
  parent?: string
  message?: string
}

type APISendActionsRequestV0 = {
  actions?: APIActionResultV0[]
}

// API v1

type APIActionV1 = {
  id: string
  name: BuilderGenAIActionTypes
  args: Record<string, unknown>
}

type APIActionResultV1 = {
  id: string
  value: unknown
}

type APIRequestV1 = {
  input?: string
  vars?: Record<string, unknown>
  parent?: string
  actions?: APIActionResultV1[]
}

type APIResponseV1 =
  | {
      type: 'response'
      id: string
      message?: string
      actions?: APIActionV1[]
    }
  | {
      type: 'delta'
      delta: string
    }
  | {
      type: 'action_execution'
      action: string
      action_id: string
      args: Record<string, unknown>
    }
  | {
      type: 'action_execution_finished'
      action: string
      action_id: string
    }
  | {
      type: 'error'
      message: string
      metadata?: Record<string, unknown>
    }
  | { type: 'end' }

///////////////////////////////////////////////////////////////////////////////
// TYPE CONVERSIONS
//
// Converts Builder-internal types to/from API v0 and v1 types.

// API v0

function toBuilderResponsesV0(response: APIResponseV0, parent: string): BuilderGenAIResponse[] {
  const responses: BuilderGenAIResponse[] = []

  responses.push({
    parent,
    type: 'response',
    message: response.message || ''
  })

  if (response.actions?.length) {
    responses.push({
      type: 'action_frontend',
      actions: response.actions.map(toBuilderActionV0)
    })
  }

  return responses
}

function toBuilderActionV0(action: APIActionV0): BuilderGenAIAction {
  return {
    id: action.parent,
    action: action.action,
    params: action.params
  }
}

// API v1

function toBuilderResponsesV1(response: APIResponseV1): BuilderGenAIResponse[] {
  const responses: BuilderGenAIResponse[] = []
  switch (response.type) {
    case 'delta':
      responses.push({
        type: 'delta',
        message: response.delta
      })
      break

    case 'response':
      responses.push({
        type: 'response',
        parent: response.id,
        message: response.message as string
      })
      if (response.actions?.length) {
        responses.push({
          type: 'action_frontend',
          actions: response.actions.map(toBuilderActionV1)
        })
      }
      break

    case 'action_execution':
      responses.push({
        type: 'action_backend',
        action: toBuilderBackendActionTypeV1(response.action)
      })
      break

    case 'error':
      responses.push({ type: response.type, error: BuilderGenAISystemMessage.UnspecifiedError })
      break

    default:
      throw new Error(`Unexpected message type, "${response.type}"`)
  }

  return responses
}

function toBuilderActionV1(action: APIActionV1): BuilderGenAIAction {
  return {
    id: action.id,
    action: toBuilderFrontendActionTypeV1(action.name),
    params: action.args
  }
}

// Agents use versioned tool names, ending `vMAJOR_MINOR_PATCH`. The frontend
// doesn't yet implement different tool versions, and can just strip the suffix.
const TOOL_VERSION_REGEX = /_v[0-9]+_[0-9]+_[0-9]+$/

function toBuilderFrontendActionTypeV1(action: string): BuilderGenAIActionTypes {
  return action.replace(TOOL_VERSION_REGEX, '') as BuilderGenAIActionTypes
}

function toBuilderBackendActionTypeV1(action: string): BuilderGenAIBackendActionTypes {
  return action.replace(TOOL_VERSION_REGEX, '') as BuilderGenAIBackendActionTypes
}
